import { useSelector } from 'react-redux';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@taxdown/zeno-sama-query';
import CheckoutService from 'Services/checkout';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { useGetQuery } from 'Hooks/useGetQuery';
import { openLinkInNewTab } from 'Utils/links';
import { DomiciliationStatus, stages } from 'Constants/stages';
import {
  CreateDomiciliationResponse,
  QueryKeys,
  ResponseArrayType,
  ResponseType,
  UserAutorizedDomiciliationsResponse,
  UserChargementsResponse,
} from 'Types/common';

export const useCreateDomiciliation = (): UseMutationResult<
  ResponseType<CreateDomiciliationResponse>,
  unknown,
  unknown,
  unknown
> =>
  useMutation([QueryKeys.CREATE_DOMICILIATION], CheckoutService.createDomiciliation, {
    onSuccess: data => {
      const druoLink = data.data.item.activationLink;
      openLinkInNewTab(druoLink);
    },
  });

export const useGetUserAutorizedDomiciliations = (): UseQueryResult<
  ResponseArrayType<UserAutorizedDomiciliationsResponse>,
  unknown
> =>
  useQuery(
    [QueryKeys.GET_USER_AUTORIZED_DOMICILIATIONS],
    CheckoutService.getUserAutorizedDomiciliations
  );

export const useGetChargements = (): UserChargementsResponse | undefined => {
  const data = useGetQuery<ResponseType<UserChargementsResponse>>([QueryKeys.GET_USER_CHARGEMENTS]);
  return data?.data?.item;
};
export const useGetDomiciliationBannerInfo = (): {
  isManualPayment: boolean;
  domiciliationIsAuthorized: boolean;
  isCard: boolean;
  needAuthorization: boolean;
  isLoading: boolean;
  userChargements: UserChargementsResponse | undefined;
  fee: number;
} => {
  const userStage = useSelector(userStageSelector);
  const { data: userAutorizedDomiciliations, isLoading: isLoadingUserAutorizedDomiciliations } =
    useGetUserAutorizedDomiciliations();
  const userChargements = useGetChargements();
  const isManualPayment =
    userChargements?.actionStatus === DomiciliationStatus.PENDING_MANUAL_PAYMENT;
  const domiciliationAuthorized =
    userAutorizedDomiciliations?.data &&
    userAutorizedDomiciliations?.data?.length > 0 &&
    userAutorizedDomiciliations?.data?.some(domiciliation => domiciliation.item.isActive) &&
    !isManualPayment;
  const isCard = userChargements?.latestChargement?.chargementMethod === 'cardAuthorization';
  const fee = userChargements?.debt?.dueAmount || 0;

  const domiciliationIsAuthorized =
    (domiciliationAuthorized && fee > 0 && userStage === stages.FINISHED_ACCEPTED) || false;

  const needAuthorization = !isManualPayment && !domiciliationAuthorized;

  return {
    isManualPayment,
    domiciliationIsAuthorized,
    isCard,
    needAuthorization,
    isLoading: isLoadingUserAutorizedDomiciliations,
    userChargements,
    fee,
  };
};
